(function (global) {
  'use strict';

  function bool(value) {
    return value === true || value === 'true';
  }

  function controlsFor(root) {
    return Array.prototype.slice.call(root.querySelectorAll('[data-lt-control]'));
  }

  function selectedValues(control) {
    var renderer = control.dataset.ltRenderer;
    if (renderer === 'balloon_swatches' || renderer === 'choice_chips') {
      return Array.prototype.slice
        .call(control.querySelectorAll('input[data-lt-property-input]:checked'))
        .map(function (input) { return input.value; });
    }
    var input = control.querySelector('[data-lt-property-input]');
    if (!input) return [];
    var raw = input.value;
    if (typeof raw !== 'string') return raw ? [raw] : [];
    // A whitespace-only entry is empty, not a value. Normalize the free-text
    // renderers in place so FormData serializes exactly what was validated;
    // choice values are contract-owned and are never rewritten.
    var value = raw.trim();
    if (value !== raw && (renderer === 'text' || renderer === 'textarea')) input.value = value;
    return value ? [value] : [];
  }

  function clearInvalid(control) {
    control.classList.remove('is-invalid');
    var error = control.querySelector('[data-lt-control-error]');
    if (error) error.hidden = true;
    Array.prototype.forEach.call(
      control.querySelectorAll('[aria-invalid="true"]'),
      function (input) { input.removeAttribute('aria-invalid'); }
    );
  }

  function markInvalid(control, message) {
    control.classList.add('is-invalid');
    var error = control.querySelector('[data-lt-control-error]');
    if (error) {
      error.textContent = message;
      error.hidden = false;
    }
    var focusTarget =
      control.querySelector('.lt-select__button') ||
      control.querySelector('[data-lt-property-input]');
    if (focusTarget) focusTarget.setAttribute('aria-invalid', 'true');
    return focusTarget || control;
  }

  function attribute(element, name) {
    if (!element) return null;
    if (typeof element.getAttribute === 'function') return element.getAttribute(name);
    return element[name] || null;
  }

  function propertyInputs(control) {
    return Array.prototype.slice.call(
      control.querySelectorAll('[data-lt-property-input]')
    );
  }

  function dependencyRequirement(control) {
    var kind = control.dataset.ltDependencyKind;
    if (!kind) return { valid: true, required: false, active: true };
    if (kind !== 'product_option_required_unless') {
      return {
        valid: false,
        required: true,
        message: 'Customization validation is unavailable. Please reload the page.'
      };
    }
    var optionName = control.dataset.ltDependencyOptionName;
    var inactiveValues = String(control.dataset.ltDependencyInactiveValues || '').split('~~');
    if (!optionName || !inactiveValues.length || inactiveValues.some(function (value) { return !value; })) {
      return {
        valid: false,
        required: true,
        message: 'Customization validation is unavailable. Please reload the page.'
      };
    }
    var root = control.closest && control.closest('[data-lt-properties-root]');
    var formId = root && root.dataset.ltFormId;
    var documentRef = (root && root.ownerDocument) || global.document;
    if (!formId || !documentRef || !documentRef.querySelectorAll) {
      return {
        valid: false,
        required: true,
        message: 'Customization validation is unavailable. Please reload the page.'
      };
    }
    var found = false;
    var selectedValue = null;
    Array.prototype.some.call(documentRef.querySelectorAll('[data-option-name]'), function (candidate) {
      if (!candidate.dataset || candidate.dataset.optionName !== optionName) return false;
      if (attribute(candidate, 'form') !== formId) return false;
      found = true;
      if (String(candidate.type || '').toLowerCase() === 'radio') {
        if (!candidate.checked) return false;
      }
      selectedValue = candidate.value;
      return selectedValue !== null && selectedValue !== undefined;
    });
    if (!found || selectedValue === null || selectedValue === undefined) {
      return { valid: false, required: true, message: 'Customization validation is unavailable. Please reload the page.' };
    }
    var active = inactiveValues.indexOf(String(selectedValue)) === -1;
    return { valid: true, required: active, active: active };
  }

  // Which inputs of a conditional control carry the server-rendered native
  // `required`, and so must keep it in step when the dependency is resolved
  // again on the client. Mirrors lt-product-extras.liquid exactly: an enhanced
  // select is excluded because lt-select.js deliberately transfers `required`
  // off the now-hidden native control and guards the submit itself, and
  // balloon_swatches is excluded because it keeps its own native fallback.
  function ownsNativeRequired(control, input) {
    if (input.dataset && input.dataset.ltListboxReady) return false;
    var renderer = control.dataset.ltRenderer;
    if (renderer === 'text' || renderer === 'textarea' || renderer === 'select') return true;
    if (renderer === 'choice_chips') return bool(control.dataset.ltMulti) === false;
    return false;
  }

  function synchronizeDependency(control, requirement) {
    if (!requirement.valid) return;
    var baseRequired = bool(control.dataset.ltRequired);
    var required = baseRequired || requirement.required;
    var conditional = Boolean(control.dataset.ltDependencyKind);
    var marker = control.querySelector('[data-lt-conditional-required-marker]');
    if (marker) marker.hidden = !required;
    propertyInputs(control).forEach(function (input) {
      input.disabled = !requirement.active;
      if (!requirement.active) {
        if (typeof input.checked === 'boolean') input.checked = false;
        if (typeof input.value === 'string') input.value = '';
      }
      if (required) input.setAttribute('aria-required', 'true');
      else input.removeAttribute('aria-required');
      // The server resolves the dependency for the rendered variant, but a
      // client-side variant change leaves that attribute stale. When
      // product-form.js never upgraded, native validation is the only
      // enforcement left, so the attribute has to follow the requirement.
      if (!conditional || !ownsNativeRequired(control, input)) return;
      if (required) input.setAttribute('required', 'required');
      else input.removeAttribute('required');
    });
    var visibleSelectButton = control.querySelector('.lt-select__button');
    if (visibleSelectButton) {
      visibleSelectButton.disabled = !requirement.active;
      if (required) visibleSelectButton.setAttribute('aria-required', 'true');
      else visibleSelectButton.removeAttribute('aria-required');
    }
  }

  function synchronizeDependencies(root) {
    controlsFor(root).forEach(function (control) {
      synchronizeDependency(control, dependencyRequirement(control));
    });
  }

  function validateControl(control) {
    clearInvalid(control);
    var renderer = control.dataset.ltRenderer;
    var baseRequired = bool(control.dataset.ltRequired);
    var requirement = dependencyRequirement(control);
    if (!requirement.valid) {
      return { valid: false, focusTarget: markInvalid(control, requirement.message) };
    }
    synchronizeDependency(control, requirement);
    var required = baseRequired || requirement.required;
    var minimum = Number(control.dataset.ltMin || 0);
    var maximum = Number(control.dataset.ltMax || 1);
    if (required && minimum < 1) minimum = 1;
    var values = selectedValues(control);

    if (renderer === 'choice_chips' && bool(control.dataset.ltMulti)) {
      return {
        valid: false,
        focusTarget: markInvalid(control, 'Multiple-choice serialization is unavailable for this option.')
      };
    }

    if (!Number.isInteger(minimum) || !Number.isInteger(maximum) || minimum < 0 || maximum < 1 || minimum > maximum) {
      return {
        valid: false,
        focusTarget: markInvalid(control, 'This customization option is unavailable.')
      };
    }

    if (renderer === 'balloon_swatches') {
      var group = control.querySelector('[data-lt-swatch-limit]');
      if (!group || typeof group.ltValidate !== 'function') {
        return {
          valid: false,
          focusTarget: markInvalid(control, 'Color selection validation is unavailable. Please reload the page.')
        };
      }
      var swatchResult = group.ltValidate();
      if (!swatchResult.valid) {
        return {
          valid: false,
          focusTarget: markInvalid(control, swatchResult.message)
        };
      }
      return { valid: true };
    }

    if ((required || minimum > 0) && values.length < minimum) {
      return {
        valid: false,
        focusTarget: markInvalid(
          control,
          minimum > 1 ? 'Please choose at least ' + minimum + ' options.' : 'Please choose an option.'
        )
      };
    }
    if (values.length > maximum) {
      return {
        valid: false,
        focusTarget: markInvalid(control, 'Please choose no more than ' + maximum + ' options.')
      };
    }
    return { valid: true };
  }

  function rootFor(form) {
    if (!form || !form.ownerDocument) return null;
    var formId = typeof form.getAttribute === 'function'
      ? form.getAttribute('id')
      : form.id;
    if (!formId || typeof formId !== 'string') return null;
    return form.ownerDocument.querySelector(
      '[data-lt-properties-root][data-lt-form-id="' + formId.replace(/"/g, '\\"') + '"]'
    );
  }

  function validate(form) {
    var root = rootFor(form);
    if (!root) {
      return {
        valid: false,
        message: 'Customization validation is unavailable. Please reload the page.'
      };
    }
    if (root.dataset.ltContractState !== 'valid') {
      var contractError = root.querySelector('[data-lt-contract-error]');
      return {
        valid: false,
        message: 'Customization options are temporarily unavailable.',
        focusTarget: contractError || root
      };
    }
    var controls = controlsFor(root);
    var firstInvalid = null;
    controls.forEach(function (control) {
      var result = validateControl(control);
      if (!result.valid && !firstInvalid) firstInvalid = result.focusTarget || control;
    });
    if (firstInvalid) {
      if (typeof firstInvalid.focus === 'function') firstInvalid.focus();
      return {
        valid: false,
        message: 'Please complete the required customization options.',
        focusTarget: firstInvalid
      };
    }
    return { valid: true };
  }

  function init(root) {
    var scope = root && root.querySelectorAll ? root : global.document;
    if (!scope) return;
    Array.prototype.forEach.call(
      scope.querySelectorAll('[data-lt-properties-root]'),
      function (propertiesRoot) {
        if (propertiesRoot.dataset.ltPropertiesReady) return;
        propertiesRoot.addEventListener('change', function (event) {
          var control = event.target.closest && event.target.closest('[data-lt-control]');
          if (control) validateControl(control);
        });
        synchronizeDependencies(propertiesRoot);
        var documentRef = propertiesRoot.ownerDocument || global.document;
        if (documentRef && typeof documentRef.addEventListener === 'function') {
          documentRef.addEventListener('change', function (event) {
            var target = event.target;
            if (!target || !target.dataset || !target.dataset.optionName) return;
            if (attribute(target, 'form') !== propertiesRoot.dataset.ltFormId) return;
            synchronizeDependencies(propertiesRoot);
          });
        }
        propertiesRoot.dataset.ltPropertiesReady = 'true';
      }
    );
    if (global.document && typeof global.CustomEvent === 'function') {
      global.document.dispatchEvent(new global.CustomEvent('lt:product-properties-ready'));
    }
  }

  var api = {
    clearInvalid: clearInvalid,
    init: init,
    markInvalid: markInvalid,
    validate: validate,
    validateControl: validateControl,
    synchronizeDependencies: synchronizeDependencies
  };
  global.LTProductProperties = api;

  if (typeof module !== 'undefined' && module.exports) module.exports = api;

  if (global.document) {
    if (global.document.readyState === 'loading') {
      global.document.addEventListener('DOMContentLoaded', function () { init(global.document); });
    } else {
      init(global.document);
    }
    global.document.addEventListener('shopify:section:load', function (event) {
      init(event.target);
    });
  }
})(typeof window !== 'undefined' ? window : globalThis);
